/**
 * Compact summary of one LinkedIn chat (category, last activity, quick actions) for lead context.
 *
 * Where: `LeadOverview` and `ChatsList`.
 * Uses: category catalog for labels/colors.
 * Behavior: navigates to inbox or stream via callbacks; read-only display.
 */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import { 
  LinkedIn as LinkedInIcon,
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
  Reply as ReplyIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Archive as ArchiveIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  Alarm as AlarmIcon,
  Send as SendIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { getCategoryLabel, getCategoryColor } from '@/constants/conversationCategoryCatalog';

interface ChatPreviewCardProps {
  chat: any;
  onOpenChat: (chatUuid: string) => void;
  onOpenStream?: (streamUuid: string) => void;
}

export const ChatPreviewCard: React.FC<ChatPreviewCardProps> = ({ 
  chat, 
  onOpenChat,
  onOpenStream,
}) => {
  const lastActivity = chat.last_activity ? new Date(chat.last_activity).toLocaleDateString() : null;
  const lastMessage = chat.messages?.items?.[chat.messages.items.length - 1];
  const isUserLastSender = lastMessage?.is_sender;
  const hasResponded = lastMessage && !isUserLastSender;
  const isUnread = !chat.seen_last_message;

  return (
    <Box 
      sx={{ 
        p: 1.5, 
        border: 1, 
        borderColor: chat.is_important ? 'warning.main' : (isUnread ? 'error.light' : 'divider'), 
        borderRadius: 1,
        bgcolor: isUnread ? 'error.50' : undefined,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {chat.brief || chat.notes || 'Conversation'}
            </Typography>
            {chat.conversation_state && (
              <Chip
                label={getCategoryLabel(chat.conversation_state)}
                size="small"
                sx={{
                  bgcolor: getCategoryColor(chat.conversation_state),
                  color: '#fff',
                  fontSize: '0.65rem',
                  height: '18px',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {chat.source && <Chip icon={<LinkedInIcon />} size="small" label={chat.source} variant="outlined" />}
            {lastActivity && <Chip icon={<AccessTimeIcon />} size="small" label={lastActivity} variant="outlined" />}
            {isUnread && <Chip icon={<CancelIcon />} size="small" label="Unread" color="error" />}
            {isUserLastSender && !hasResponded && <Chip icon={<ReplyIcon />} size="small" label="Waiting" color="warning" />}
            {hasResponded && <Chip icon={<CheckCircleIcon />} size="small" label="Responded" color="success" />}
            {chat.is_important && <Chip icon={<StarIcon />} size="small" color="warning" />}
            {chat.is_archived && <Chip icon={<ArchiveIcon />} size="small" variant="outlined" />}
          </Box>
          {(chat.message_categories || []).length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {chat.message_categories.slice(0, 3).map((mc: any) => (
                <Chip key={mc.key} size="small" label={mc.label} sx={{ bgcolor: mc.color || undefined }} />
              ))}
            </Box>
          )}
        </Box>
        <Button size="small" onClick={() => onOpenChat(chat.key || chat.uuid)} endIcon={<ChatIcon />}>Open</Button>
      </Box>
      
      {/* Contact info & Meeting */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {chat.email && (
          <Chip icon={<MailIcon />} size="small" label={chat.email} variant="outlined" />
        )}
        {chat.phone && (
          <Chip icon={<PhoneIcon />} size="small" label={chat.phone} variant="outlined" />
        )}
        {chat.meeting_time && (
          <Chip icon={<EventIcon />} size="small" label={chat.meeting_time} color="info" />
        )}
        {chat.followup_date && (
          <Chip icon={<AlarmIcon />} size="small" label={`Follow-up: ${chat.followup_date}`} color="primary" />
        )}
      </Box>
      
      {/* Last 2 messages preview */}
      {chat.messages?.items && chat.messages.items.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {chat.messages.items.slice(-2).map((msg: any, idx: number) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
              {msg.is_sender ? <SendIcon fontSize="small" color="action" /> : <ReplyIcon fontSize="small" color="action" />}
              <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {msg.text}
              </Typography>
              {msg.received_date && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {new Date(msg.received_date).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
      
      {/* Project/Stream links */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {chat.stream && chat.stream.key && onOpenStream && (
          <Chip size="small" label={chat.stream.name} onClick={() => onOpenStream(chat.stream.key)} />
        )}
      </Box>
    </Box>
  );
};


